import { supabase } from "@/integrations/supabase/client";

export interface FreeEbookLink {
  id: string;
  book_title: string;
  book_author: string;
  isbn?: string;
  gutenberg_url?: string;
  archive_url?: string;
  gutenberg_id?: string;
  archive_id?: string;
  formats?: Record<string, string>;
  last_checked: string;
}

export interface EbookSearchResult {
  hasLinks: boolean;
  gutenberg?: {
    url: string;
    id: string;
    formats: Record<string, string>;
  };
  archive?: {
    url: string;
    id: string;
    formats: Record<string, string>;
  };
}

/**
 * Which source to send a reader to, when a book has more than one.
 *
 * Prefer Project Gutenberg: it is public domain and reads in full. Most of this
 * catalogue's Internet Archive holdings are lending copies needing an account
 * and a waitlist — 19 of 20 sampled carried `access-restricted-item`.
 *
 * This lives here because the preference was written out by hand in FOUR
 * places (the chip's label, the preview modal's click handler, that modal's
 * button label, and the ebook modal's layout) and three of them had it
 * backwards, so a book with a readable Gutenberg edition still advertised —
 * and opened — the borrow-only one. One rule, one home.
 */
export const preferredEbookSource = (
  e: EbookSearchResult | null | undefined
): { kind: 'gutenberg' | 'archive'; url: string; label: string } | null => {
  if (e?.gutenberg?.url) {
    return { kind: 'gutenberg', url: e.gutenberg.url, label: 'Project Gutenberg' };
  }
  if (e?.archive?.url) {
    return { kind: 'archive', url: e.archive.url, label: 'Internet Archive' };
  }
  return null;
};

/**
 * Search for free ebook links in cached data
 */
export const getCachedFreeEbookLink = async (
  title: string, 
  author: string, 
  isbn?: string
): Promise<FreeEbookLink | null> => {
  try {
    // Prefer ISBN exact match
    if (isbn) {
      const { data: isbnResult, error: isbnErr } = await supabase
        .from('free_ebook_links')
        .select('*')
        .eq('isbn', isbn)
        // Newest row wins. maybeSingle() errored whenever two rows shared an
        // ISBN (case-variant duplicates), silently skipping the ISBN path.
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (isbnErr && isbnErr.code !== 'PGRST116') {
        console.error('Error fetching by ISBN from cache:', isbnErr);
      }
      if (isbnResult) {
        return {
          ...isbnResult,
          formats: typeof (isbnResult as any).formats === 'string'
            ? JSON.parse((isbnResult as any).formats)
            : (isbnResult as any).formats || {}
        } as FreeEbookLink;
      }
    }

    // Build normalized variants to tolerate punctuation/spacing differences
    const normalize = (s: string) => s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const normTitle = normalize(title);
    const normAuthor = normalize(author);

    // PostgREST's .or() logic tree treats "(", ")" and "," as syntax, so any of
    // those characters in an interpolated value produces a 400 (PGRST100) and the
    // whole cache lookup fails — meaning titles like "Vivarium (short story ...)"
    // never hit the cache and re-run the live search every render. Strip them for
    // the filter; matching is fuzzy and re-scored client-side below, so broadening
    // is harmless.
    const orSafe = (s: string) => s.replace(/[(),]/g, ' ').replace(/\s+/g, ' ').trim();
    const safeTitle = orSafe(title);
    const safeAuthor = orSafe(author);
    const authorNoDots = orSafe(author.replace(/[.]/g, ''));
    const authorTight = orSafe(author.replace(/\s+/g, ''));
    const titleNoPunct = orSafe(title.replace(/[^\w\s]/g, ' '));

    // Fetch a small candidate set using broad matching, then score client-side
    const { data: candidates, error } = await supabase
      .from('free_ebook_links')
      .select('*')
      .or(
        [
          `and(book_title.ilike.%${safeTitle}%,book_author.ilike.%${safeAuthor}%)`,
          `and(book_title.ilike.%${safeTitle}%,book_author.ilike.%${authorNoDots}%)`,
          `and(book_title.ilike.%${titleNoPunct}%,book_author.ilike.%${safeAuthor}%)`,
          `and(book_title.ilike.%${titleNoPunct}%,book_author.ilike.%${authorNoDots}%)`,
          `and(book_title.ilike.%${safeTitle}%,book_author.ilike.%${authorTight}%)`
        ].join(',')
      )
      .order('last_checked', { ascending: false })
      .limit(10);

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching cached candidates:', error);
      return null;
    }

    if (!candidates || candidates.length === 0) return null;

    // Score and pick the best candidate with stricter matching
    const scoreRow = (r: any) => {
      const rt = normalize(r.book_title || '');
      const ra = normalize(r.book_author || '');
      let score = 0;
      
      // Exact title match is critical
      if (rt === normTitle) score += 20; 
      else if (normTitle.includes(rt) || rt.includes(normTitle)) score += 8;
      
      // Author must reasonably match
      if (ra === normAuthor) score += 10; 
      else if (normAuthor.includes(ra) || ra.includes(normAuthor)) score += 4;
      
      // Bonus for having actual links
      if (r.archive_url || r.gutenberg_url) score += 5;
      
      return score;
    };

    const scored = candidates.map(c => ({ row: c, score: scoreRow(c) }));
    scored.sort((a, b) => b.score - a.score);
    const best = scored[0];

    // Require minimum score of 15 to prevent false matches
    // (exact title = 20, partial title+author = 12, which won't pass)
    if (!best || best.score < 15) {
      console.log('📚 [FreeEbook] No cached match above threshold', {
        bestScore: best?.score,
        candidateCount: candidates.length
      });
      return null;
    }

    /**
     * Coalesce the links across every row that matched this book, rather than
     * trusting the top-scoring row alone.
     *
     * The table is UNIQUE (book_title, book_author), so a book acquires one row
     * per spelling of its author, and those rows hold DIFFERENT links. Picking
     * a single winner therefore threw real links away, because an exact author
     * match (+10) outweighs actually having links (+5) — measured against live
     * data:
     *
     *   Masters of Space  "E.E. Smith"      no links  30  <- won
     *                     "E Smith Edward"  archive   25     so the site said
     *                                                        "no free ebooks"
     *   Flatland          "Edwin A. Abbott" archive   35  <- won
     *                     "Edwin Abbott"    archive +
     *                                       gutenberg 25     so the readable
     *                                                        edition was hidden
     *
     * These rows are fragments of one book's availability, not rivals. Filling
     * the gaps keeps the negative cache intact: if every matching row is empty
     * there is still nothing to fill with, so callers still see no links and
     * still skip the live search (see the negative-cache branch below).
     */
    const merged: any = { ...(best.row as any) };
    for (const { row, score } of scored) {
      if (score < 15 || row === best.row) continue;
      const r = row as any;
      if (!merged.archive_url && r.archive_url) {
        merged.archive_url = r.archive_url;
        merged.archive_id = merged.archive_id ?? r.archive_id;
      }
      if (!merged.gutenberg_url && r.gutenberg_url) {
        merged.gutenberg_url = r.gutenberg_url;
        merged.gutenberg_id = merged.gutenberg_id ?? r.gutenberg_id;
      }
    }

    return {
      ...merged,
      formats: typeof merged.formats === 'string'
        ? JSON.parse(merged.formats)
        : merged.formats || {}
    } as FreeEbookLink;
  } catch (error) {
    console.error('Error in getCachedFreeEbookLink:', error);
    return null;
  }
};

/**
 * Search for free ebooks using edge function
 */
export const searchFreeEbooks = async (
  title: string,
  author: string,
  isbn?: string,
  options: { forceRefresh?: boolean } = {}
): Promise<EbookSearchResult> => {
  const startTime = Date.now();
  const searchKey = `${title}-${author}-${isbn || 'no-isbn'}`;
  
  console.log(`🔍 [FreeEbook] Starting search for: "${title}" by ${author}`, {
    title,
    author,
    isbn,
    searchKey: searchKey.substring(0, 50) + '...'
  });

  try {
    // Check cache unless force refresh
    if (!options.forceRefresh) {
      console.log('📦 [FreeEbook] Checking cache...');
      const cached = await getCachedFreeEbookLink(title, author, isbn);
      
      // If cached and recent (within 24 hours for negative, 7 days for positive), use it
      if (cached) {
        const lastChecked = new Date(cached.last_checked);
        const hasPositiveResult = !!(cached.gutenberg_url || cached.archive_url);
        const cacheTTL = hasPositiveResult
          ? 7 * 24 * 60 * 60 * 1000        // 7 days for positive results
          : 30 * 24 * 60 * 60 * 1000;      // 30 days for negative results (a book without a free edition won't gain one hourly)
        const cacheExpiry = new Date(Date.now() - cacheTTL);
        const age = Date.now() - lastChecked.getTime();
        const isFresh = lastChecked > cacheExpiry;

        console.log(`💾 [FreeEbook] Cache entry found, age: ${Math.round(age / (1000 * 60 * 60))}h`, {
          hasGutenberg: !!cached.gutenberg_url,
          hasArchive: !!cached.archive_url,
          isPositive: hasPositiveResult,
          ttlHours: cacheTTL / (1000 * 60 * 60),
          lastChecked: cached.last_checked
        });

        if (isFresh && hasPositiveResult) {
          console.log('✅ [FreeEbook] Using cached positive result');
          return {
            hasLinks: hasPositiveResult,
            gutenberg: cached.gutenberg_url ? {
              url: cached.gutenberg_url,
              id: cached.gutenberg_id || '',
              formats: cached.formats || {}
            } : undefined,
            archive: cached.archive_url ? {
              url: cached.archive_url,
              id: cached.archive_id || '',
              formats: cached.formats || {}
            } : undefined
          };
        }
        if (isFresh && !hasPositiveResult) {
          // Fresh negative result — trust it and skip the live edge-function call.
          // (Previously this branch fell through and re-hit search-free-ebooks on
          // every render, so the negative cache was never actually used.)
          console.log('🚫 [FreeEbook] Fresh negative cache; skipping live search');
          return { hasLinks: false };
        }
        console.log('⏰ [FreeEbook] Cache expired; refreshing...');
      } else {
        console.log('❌ [FreeEbook] No cache entry found');
      }
    } else {
      console.log('🔄 [FreeEbook] Force refresh requested, bypassing cache');
    }

    // The live search (search-free-ebooks) and the negative-cache writes both
    // reject anonymous callers (401). For logged-out visitors this means every
    // uncached book fires a guaranteed-failing edge invoke + write on each
    // render — a major source of request volume. Bail out early instead; any
    // cached positive above is still served to anon users.
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log('👤 [FreeEbook] Anonymous session; skipping live search');
      return { hasLinks: false };
    }

    // Prepare search terms with transliteration for non-English titles
    const searchTerms = prepareSearchTerms(title, author);
    console.log('🔤 [FreeEbook] Search terms prepared:', searchTerms);

    // Search using edge function with timeout
    console.log('🌐 [FreeEbook] Calling edge function...');
    const searchPromise = supabase.functions.invoke('search-free-ebooks', {
      body: { 
        title, 
        author, 
        isbn,
        searchTerms,
        priority: determinePriority(title, author)
      }
    });

    // Add timeout to prevent hanging requests
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Search timeout after 30s')), 30000);
    });

    const { data, error } = await Promise.race([searchPromise, timeoutPromise]);
    const responseTime = Date.now() - startTime;

    if (error) {
      console.error('💥 [FreeEbook] Edge function error:', error);
      console.log(`⏱️ [FreeEbook] Failed after ${responseTime}ms`);
      
      // Cache negative result to avoid repeated failed searches
      await cacheNegativeResult(title, author, isbn);
      return { hasLinks: false };
    }

    const result = data || { hasLinks: false };
    console.log(`✅ [FreeEbook] Search completed in ${responseTime}ms:`, {
      hasLinks: result.hasLinks,
      hasGutenberg: !!result.gutenberg,
      hasArchive: !!result.archive,
      responseTime
    });

    return result;
  } catch (error) {
    const responseTime = Date.now() - startTime;
    console.error(`💥 [FreeEbook] Search failed after ${responseTime}ms:`, error);
    
    // Cache negative result for network errors too
    await cacheNegativeResult(title, author, isbn);
    return { hasLinks: false };
  }
};

/**
 * Prepare search terms with transliteration and alternatives
 */
const prepareSearchTerms = (title: string, author: string) => {
  const terms = {
    title: [title],
    author: [author]
  };
  
  // Add simplified versions for non-English titles
  if (!/^[a-zA-Z0-9\s.,!?'-]+$/.test(title)) {
    // Remove special characters and diacritics
    const simplified = title
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[^\w\s]/g, ' ')         // Remove special chars
      .replace(/\s+/g, ' ')             // Normalize spaces
      .trim();
    
    if (simplified !== title) {
      terms.title.push(simplified);
      console.log('🔤 [FreeEbook] Added simplified title:', simplified);
    }
  }
  
  return terms;
};

/**
 * Determine search priority based on title/author patterns
 */
const determinePriority = (title: string, author: string): 'high' | 'normal' | 'low' => {
  // High priority for classic authors likely to be in public domain
  const classicAuthors = ['asimov', 'herbert', 'clarke', 'heinlein', 'verne', 'wells'];
  if (classicAuthors.some(classic => author.toLowerCase().includes(classic))) {
    return 'high';
  }
  
  // Low priority for very recent books or non-English titles
  if (!/^[a-zA-Z0-9\s.,!?'-]+$/.test(title)) {
    return 'low';
  }
  
  return 'normal';
};

/**
 * Cache negative result to avoid repeated failed searches
 */
const cacheNegativeResult = async (title: string, author: string, isbn?: string) => {
  try {
    // Replace any existing entry for this title/author to avoid duplicates
    await supabase
      .from('free_ebook_links')
      .delete()
      .eq('book_title', title)
      .eq('book_author', author);

    await supabase.from('free_ebook_links').insert({
      book_title: title,
      book_author: author,
      isbn: isbn,
      last_checked: new Date().toISOString(),
      gutenberg_url: null,
      archive_url: null,
      formats: {}
    });
    console.log('💾 [FreeEbook] Cached negative result');
  } catch (error) {
    console.error('💥 [FreeEbook] Failed to cache negative result:', error);
  }
};

/**
 * Download a free ebook using fetch to handle cross-origin issues
 */
export const downloadFreeEbook = async (
  url: string,
  filename: string,
  source: 'gutenberg' | 'archive'
): Promise<boolean> => {
  try {
    console.log(`Attempting to download: ${url}`);
    
    // Try to fetch the file and create a blob for download
    const response = await fetch(url, {
      mode: 'cors',
      headers: {
        'Accept': '*/*'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    
    // Create download link
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.style.display = 'none';
    
    // Add to DOM, click, and cleanup
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Cleanup blob URL
    window.URL.revokeObjectURL(blobUrl);
    
    console.log(`Free ebook download completed: ${filename} from ${source}`);
    return true;
  } catch (error) {
    console.error('Blob download failed, trying direct link:', error);
    
    // Fallback to direct link if fetch fails
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`Direct link download initiated: ${filename} from ${source}`);
      return true;
    } catch (fallbackError) {
      console.error('Error downloading free ebook:', fallbackError);
      return false;
    }
  }
};

/**
 * Get preferred download format and URL
 */
export const getPreferredDownloadUrl = (formats: Record<string, string>): { url: string; format: string } | null => {
  // Preference order: EPUB > PDF > TXT for better compatibility
  const preferenceOrder = ['epub', 'pdf', 'txt'];
  
  for (const format of preferenceOrder) {
    // Check for exact match or partial match (e.g., 'epub.noimages', 'pdf.compressed')
    const matchingFormat = Object.keys(formats).find(key => 
      key.toLowerCase().includes(format.toLowerCase())
    );
    
    if (matchingFormat && formats[matchingFormat]) {
      return { url: formats[matchingFormat], format: format };
    }
  }
  
  // If no preferred format, return first available
  const firstFormat = Object.keys(formats)[0];
  if (firstFormat) {
    const cleanFormat = firstFormat.toLowerCase().includes('epub') ? 'epub' : 
                       firstFormat.toLowerCase().includes('pdf') ? 'pdf' : 
                       firstFormat.toLowerCase().includes('txt') ? 'txt' : firstFormat;
    return { url: formats[firstFormat], format: cleanFormat };
  }
  
  return null;
};